// Provider grade vocabulary — `Provider.grade` (a plain String column).
//
// One stored code carries up to three facts, joined with "_" in this order:
//
//   <grade>_<academicTitle>_phd     e.g. "senior_specialist_associate_professor_phd"
//
// - grade: the professional grade of a physician / dentist —
//     resident           RO "medic rezident",  BG "специализант"
//     specialist         RO "medic specialist", BG "специалист"
//     senior_specialist  RO "medic primar" (no BG equivalent)
// - academicTitle: a university title, highest one only —
//     academician, professor, associate_professor (RO conferențiar, BG доцент),
//     lecturer (RO șef de lucrări / lector, BG главен асистент),
//     assistant_professor (RO asistent universitar, BG асистент)
// - phd: a doctorate (RO "doctor în științe medicale" / "doctor în medicină",
//   the "dr." after a university title in "Prof. univ. dr."; BG "доктор").
//   Labelled as a medical doctorate only for professions with the doctor
//   title — see `gradeDisplayParts`.
//
// Every part is optional, so "professor" (BG sources state the title only)
// and "specialist" are both valid codes. Kept in one column on purpose: no
// schema change, and the parts are parsed back exactly because each list is
// closed. Rows written before 2026-10-23 hold older spellings ("medic_primar",
// "rezident", "primary", "medic_primar_profesor_universitar",
// "chief_assistant_professor", …) — `normalizeGrade` maps them all, and every
// display path reads through `parseGrade`, so a legacy row still shows its label.

import { hasDoctorTitle } from "./provider-title";

/** Professional grades, lowest first. */
export const PROVIDER_GRADES = ["resident", "specialist", "senior_specialist"] as const;
export type ProviderGrade = (typeof PROVIDER_GRADES)[number];

/** University titles, lowest first. */
export const ACADEMIC_TITLES = [
  "assistant_professor",
  "lecturer",
  "associate_professor",
  "professor",
  "academician",
] as const;
export type AcademicTitle = (typeof ACADEMIC_TITLES)[number];

/** The suffix that marks a doctorate. */
export const PHD_CODE = "phd";

export interface ParsedGrade {
  grade: ProviderGrade | null;
  academicTitle: AcademicTitle | null;
  phd: boolean;
}

// Longest first, so "senior_specialist" never parses as "senior" + "specialist".
const GRADES_BY_LENGTH = [...PROVIDER_GRADES].sort((a, b) => b.length - a.length);
const TITLES_BY_LENGTH = [...ACADEMIC_TITLES].sort((a, b) => b.length - a.length);

/** Builds the stored code from its parts; null when there is no part. */
export function composeGrade(parts: Partial<ParsedGrade>): string | null {
  const out = [parts.grade, parts.academicTitle, parts.phd ? PHD_CODE : null].filter(
    (p): p is string => !!p,
  );
  return out.length ? out.join("_") : null;
}

/** Exact parse of a canonical code; null when it is not one. */
function parseCanonical(code: string): ParsedGrade | null {
  let rest = code;
  let grade: ProviderGrade | null = null;
  let academicTitle: AcademicTitle | null = null;
  let phd = false;
  const take = (word: string): boolean => {
    if (rest === word) {
      rest = "";
      return true;
    }
    if (rest.startsWith(`${word}_`)) {
      rest = rest.slice(word.length + 1);
      return true;
    }
    return false;
  };
  grade = GRADES_BY_LENGTH.find((g) => take(g)) ?? null;
  academicTitle = TITLES_BY_LENGTH.find((t) => take(t)) ?? null;
  if (take(PHD_CODE)) phd = true;
  if (rest !== "" || (!grade && !academicTitle && !phd)) return null;
  return { grade, academicTitle, phd };
}

/** Lowercase, no diacritics, words joined by "_" (Cyrillic kept). */
function fold(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9Ѐ-ӿ]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const W = (alts: string): RegExp => new RegExp(`(?:^|_)(?:${alts})(?=_|$)`, "u");

const SENIOR_RE = W("primar|primary|senior|senior_doctor|senior_specialist");
const SPECIALIST_RE = W("specialist|специалист");
const RESIDENT_RE = W("rezident|resident|rezidentiat|специализант");

/**
 * Title patterns, checked in this order: each match is cut out of the text
 * before the next one runs, so "associate_professor" and
 * "chief_assistant_professor" don't also count as "professor" /
 * "assistant_professor".
 */
const TITLE_PATTERNS: ReadonlyArray<[AcademicTitle, RegExp]> = [
  ["academician", W("academician|akademik|академик|акад")],
  [
    "associate_professor",
    W("associate_professor|assoc_prof|conferentiar(?:_universitar)?|conf(?:_univ)?|доцент|доц"),
  ],
  [
    "lecturer",
    W(
      "lecturer|sef_de_lucrari|sef_lucrari|s_l|lector(?:_universitar)?|chief_assistant(?:_professor)?|главен_асистент|гл_ас",
    ),
  ],
  [
    "assistant_professor",
    W(
      "assistant_professor|asistent_universitar|asistent(?!_medical)|asist(?:_univ)?(?!_medical)|as_univ|асистент|ас",
    ),
  ],
  ["professor", W("professor|profesor(?:_universitar)?|prof(?:_univ)?|професор|проф")],
];

const PHD_RE = W(
  "phd|dsc|doctor_in_stiinte(?:_medicale)?|doctor_stiinte(?:_medicale)?|doctor_in_medicina|doctor_medicina|доктор_на_науките|доктор_на_медицинските_науки|д_р_на_медицинските_науки",
);
/** Stands in for a title cut out of the text; `fold` never emits it. */
const TITLE_MARK = "#";
/**
 * RO "Prof. univ. dr." / "asist_univ_dr" — a "dr" right after an academic
 * title is the doctorate. A "dr" anywhere else is the physician title ("Dr."),
 * and so is BG "д-р" ("Доц. д-р" = assoc. prof., MD).
 */
const DR_AFTER_TITLE_RE = new RegExp(`${TITLE_MARK}_+dr(?=_|$)`, "u");

/** Parses any spelling (legacy or canonical); null when no grade, title or PhD is recognised. */
export function parseGrade(raw: string | null | undefined): ParsedGrade | null {
  if (raw == null) return null;
  const s = fold(String(raw));
  if (!s) return null;
  const canonical = parseCanonical(s);
  if (canonical) return canonical;

  let grade: ProviderGrade | null = null;
  if (SENIOR_RE.test(s)) grade = "senior_specialist";
  else if (SPECIALIST_RE.test(s)) grade = "specialist";
  else if (RESIDENT_RE.test(s)) grade = "resident";

  let rest = s;
  let academicTitle: AcademicTitle | null = null;
  for (const [title, re] of TITLE_PATTERNS) {
    if (re.test(rest)) {
      if (
        !academicTitle ||
        ACADEMIC_TITLES.indexOf(title) > ACADEMIC_TITLES.indexOf(academicTitle)
      ) {
        academicTitle = title;
      }
      rest = rest.replace(new RegExp(re.source, "gu"), `_${TITLE_MARK}_`);
    }
  }

  const phd = PHD_RE.test(s) || DR_AFTER_TITLE_RE.test(rest);
  if (!grade && !academicTitle && !phd) return null;
  return { grade, academicTitle, phd };
}

/**
 * The canonical code for any stored or scraped spelling, or null when it
 * names nothing known ("medic_dentist", "", junk). Idempotent:
 * `normalizeGrade(normalizeGrade(x)) === normalizeGrade(x)`.
 */
export function normalizeGrade(raw: string | null | undefined): string | null {
  const parsed = parseGrade(raw);
  return parsed ? composeGrade(parsed) : null;
}

/** True for a code that is already canonical (what the editor may save). */
export function isCanonicalGrade(code: string | null | undefined): boolean {
  return code != null && code !== "" && normalizeGrade(code) === code;
}

export interface GradeDisplayParts extends ParsedGrade {
  /**
   * The profession carries the doctor title (md_doctor, dentist): its PhD is
   * labelled as a medical doctorate (RO "Doctor în științe medicale"); any
   * other profession gets the neutral PhD label.
   */
  medical: boolean;
}

/**
 * What to label for a provider: the parts of its grade code, with the
 * professional grade dropped for professions that don't hold one
 * (psychologist, physiotherapist, …) — "Medic specialist" would misstate
 * their credentials, as would a doctorate "in medical sciences". A missing
 * profession counts as the schema default (md_doctor). Null when nothing is
 * left to show.
 */
export function gradeDisplayParts(
  code: string | null | undefined,
  profession?: string | null,
): GradeDisplayParts | null {
  const parsed = parseGrade(code);
  if (!parsed) return null;
  // Same professions as the doctor title (md_doctor, dentist; missing = md_doctor).
  const medical = hasDoctorTitle(profession);
  const out: GradeDisplayParts = { ...parsed, grade: medical ? parsed.grade : null, medical };
  return out.grade || out.academicTitle || out.phd ? out : null;
}

/** `Provider.gender` as stored: the scraper, the search filter and the portal all use these. */
export type ProviderGender = "M" | "F" | "other";

/**
 * Reads a `Provider.gender` value. Older portal builds wrote "male" / "female";
 * both spellings map to "M" / "F". Null when empty or not a gender.
 */
export function parseProviderGender(raw: string | null | undefined): ProviderGender | null {
  const value = raw?.trim().toLowerCase();
  if (value === "f" || value === "female") return "F";
  if (value === "m" || value === "male") return "M";
  if (value === "other") return "other";
  return null;
}

/**
 * `Provider.gender` "F" → the feminine forms of grades and titles, where the
 * language has them (de Fachärztin, fr professeure, it professoressa, el
 * καθηγήτρια). "M", "other" and an unknown gender keep the generic forms; ro,
 * bg, hu and en use the same words for both.
 */
export function isFemaleGender(gender: string | null | undefined): boolean {
  return parseProviderGender(gender) === "F";
}

/**
 * Joins already-translated labels for a grade, in reading order (grade, title,
 * PhD). `phd` gets whether the doctorate is a medical one (parts without
 * `medical` count as a doctor's, like a missing profession).
 */
export function joinGradeLabels(
  parts: (ParsedGrade & { medical?: boolean }) | null,
  labels: {
    grade: (g: ProviderGrade) => string;
    title: (t: AcademicTitle) => string;
    phd: (medical: boolean) => string;
  },
): string {
  if (!parts) return "";
  return [
    parts.grade ? labels.grade(parts.grade) : "",
    parts.academicTitle ? labels.title(parts.academicTitle) : "",
    parts.phd ? labels.phd(parts.medical ?? true) : "",
  ]
    .filter(Boolean)
    .join(", ");
}
